import {
  Component,
  HostListener,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  afterNextRender,
  inject,
  signal,
  viewChild,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CountryToken, LanguageToken } from '@app/core/env.tokens';
import {
  CommonModule,
  NgOptimizedImage,
  isPlatformBrowser,
} from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';

import { AuthService } from '@services/http/auth.service';
import { ExternalService } from '@app/services/http/external.service';
import { StorageService } from '@services/logic/storage.service';
import { TranslateService } from '@services/logic/translate.service';

import { VerticalLinkBoxComponent } from '@components/header/vertical-link-box/vertical-link-box.component';
import { TranslatePipe } from '@app/pipes/translate.pipe';
import { RoutePipe } from '@app/pipes/route.pipe';
import { SeoRoutePipe } from '@app/pipes/seo-route.pipe';
import { GlobalSearchEngineComponent } from '@components/seoPageElements/global-search-engine/global-search-engine.component';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { getCountryConfig, LanguageCode, LANGUAGES } from '@handyhand/i18n';
import type { StoredLanguage } from '@services/logic/storage.service';

@Component({
  imports: [
    CommonModule,
    MatMenuModule,
    MatIconModule,
    VerticalLinkBoxComponent,
    TranslatePipe,
    RoutePipe,
    SeoRoutePipe,
    NgOptimizedImage,
    GlobalSearchEngineComponent,
  ],
  providers: [
    AuthService,
    StorageService,
    TranslateService,
    TranslatePipe,
    ExternalService,
  ],
  selector: 'app-header',
  templateUrl: './header.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly storage = inject(StorageService);
  private readonly translateService = inject(TranslateService);
  private readonly defaultLanguage = inject(LanguageToken);

  protected readonly externalService = inject(ExternalService);
  protected readonly country = inject(CountryToken);

  private readonly trigger = viewChild(MatMenuTrigger);

  private readonly destroy$ = new Subject<void>();
  private readonly timeoutIds: ReturnType<typeof setTimeout>[] = [];
  private documentClickListener?: (event: Event) => void;
  private justNavigated = false;
  private innerWidth?: number;

  public readonly isBrowser = isPlatformBrowser(this.platformId);

  // Auth state is localStorage-backed, so the server can never read it and
  // always renders the logged-out branch. To avoid an SSR hydration mismatch
  // (NG0500), this signal stays `false` during SSR *and* the initial client
  // render, then syncs to the real value in `afterNextRender` — after
  // hydration has completed.
  protected readonly isLoggedIn = signal(false);
  private readonly countryDefaultLanguage: LanguageCode = getCountryConfig(
    this.country,
  ).defaultLanguage;

  // The country's default language is listed first in the picker; the rest
  // keep their LANGUAGES order.
  protected readonly languages: readonly StoredLanguage[] = LANGUAGES.filter(
    (language) => language.enabled,
  ).sort(
    (a, b) =>
      Number(b.code === this.countryDefaultLanguage) -
      Number(a.code === this.countryDefaultLanguage),
  );

  // View state — signals so change detection and SSR hydration stay in sync.
  protected readonly language = signal<StoredLanguage>(
    this.getInitialLanguage(),
  );
  protected readonly reversedFlag = signal<string | undefined>(undefined);
  protected readonly isMenuOpen = signal(false);
  protected readonly isSearchVisible = signal(false);
  protected readonly isDropdownOpen = signal(false);
  protected readonly isOddSize = signal(false);

  constructor() {
    afterNextRender(() => {
      this.storage.migrateLegacyLanguageToCookie();
      this.updateResponsiveState();

      this.setupDocumentClickListener();
      this.setupRouterNavigationListener();

      // Sync auth state only after hydration so SSR (always logged-out) and the
      // initial client render agree, preventing an NG0500 hydration mismatch.
      this.authService.authStatus$
        .pipe(takeUntil(this.destroy$))
        .subscribe((status) => this.isLoggedIn.set(status));
    });
  }

  ngOnInit(): void {
    this.setLanguageDefault();
    this.initiateLanguage(this.storage.languageCookie ?? undefined);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.cleanupDocumentClickListener();

    this.timeoutIds.forEach((id) => clearTimeout(id));
    this.timeoutIds.length = 0;
  }

  @HostListener('window:resize')
  protected onResize(): void {
    if (!this.isBrowser) {
      return;
    }

    this.updateResponsiveState();
  }

  @HostListener('window:popstate', ['$event'])
  protected onPopState(event: PopStateEvent): void {
    if (this.isBrowser && this.isMenuOpen()) {
      event.preventDefault();
      this.closeMobileMenuOnBackButton();
    }
  }

  private updateResponsiveState(): void {
    this.innerWidth = window.innerWidth;
    this.isOddSize.set(this.innerWidth > 650 && this.innerWidth < 1030);
  }

  private getInitialLanguage(): StoredLanguage {
    return (
      this.languages.find(
        (language) => language.code === this.defaultLanguage,
      ) ?? this.languages[0]
    );
  }

  private setupDocumentClickListener(): void {
    this.documentClickListener = (event: Event) => {
      const target = event.target as HTMLElement;

      // Don't interfere with scroll navigation / hero interactions.
      if (
        target.closest('.deep-link-btn') ||
        target.closest('.deep-link-buttons') ||
        target.closest('app-generic-button-with-arrow') ||
        target.closest('.hero-section') ||
        target.classList.contains('deep-link-btn')
      ) {
        return;
      }

      // Close the search when clicking outside its container.
      if (
        this.isSearchVisible() &&
        !target.closest('.header_search_container') &&
        !target.classList.contains('global-search-input')
      ) {
        this.isSearchVisible.set(false);
      }

      // Close the language dropdown when clicking outside it.
      if (this.isDropdownOpen() && !target.closest('.language_select')) {
        this.isDropdownOpen.set(false);
      }
    };

    document.addEventListener('click', this.documentClickListener, {
      passive: true,
    });
  }

  private cleanupDocumentClickListener(): void {
    if (this.documentClickListener && this.isBrowser) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = undefined;
    }
  }

  private changeLanguage(
    language: StoredLanguage,
    reloadPage: boolean = true,
  ): void {
    this.storage.languageCookie = language.code;
    this.translateService.use(language.code);
    this.language.set(language);

    if (reloadPage && this.isBrowser) {
      window.location.reload();
      return;
    }
  }

  private initiateLanguage(languageCode: LanguageCode | undefined): void {
    if (!languageCode) {
      return;
    }

    const language = this.languages.find(
      (candidate) => candidate.code === languageCode,
    );

    if (!language || language.code !== this.language().code) {
      return;
    }

    this.setLanguage(language, false);
  }

  private setLanguageDefault(): void {
    const cookieCode = this.storage.languageCookie;

    const fromCookie = cookieCode
      ? this.languages.find((language) => language.code === cookieCode)
      : undefined;

    if (fromCookie) {
      this.setLanguage(fromCookie, false);
      return;
    }

    const defaultLanguage =
      this.languages.find(
        (language) => language.code === this.defaultLanguage,
      ) ?? this.languages[0];

    this.storage.languageCookie = defaultLanguage.code;
    this.setLanguage(defaultLanguage, false);
  }

  protected setLanguage(
    language: StoredLanguage,
    reloadPage: boolean = true,
  ): void {
    this.changeLanguage(language, reloadPage);
  }

  protected switchMobileLanguage(): void {
    const currentCode = this.language().code;

    const language = this.languages.find(
      (candidate) => candidate.code !== currentCode,
    );

    if (!language) {
      return;
    }

    this.setLanguage(language, true);
  }

  protected toggleDropdown(): void {
    this.isDropdownOpen.set(!this.isDropdownOpen());
  }

  protected toggleSearch(event: MouseEvent): void {
    // Only stop propagation for search-related clicks; let other events flow.
    if (
      event.target &&
      (event.target as HTMLElement).closest('.header_search_container')
    ) {
      event.stopPropagation();
    }

    this.isSearchVisible.set(!this.isSearchVisible());

    // Close the mobile menu when opening search to prevent conflicts.
    if (this.isSearchVisible() && this.isMenuOpen()) {
      this.isMenuOpen.set(false);
      this.trigger()?.closeMenu();
    }
  }

  protected navigateTo(path: string): void {
    // Aggressively close the menu before navigating to avoid hydration glitches.
    this.isMenuOpen.set(false);
    this.justNavigated = true;
    this.trigger()?.closeMenu();

    if (this.isBrowser) {
      this.forceCloseMenuPanels();
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    // Navigate after a short delay so the menu finishes closing first.
    this.timeoutIds.push(
      setTimeout(() => {
        this.router.navigate([path]);
        this.isMenuOpen.set(false);

        // Reset the navigation flag once hydration has likely completed.
        this.timeoutIds.push(
          setTimeout(() => {
            this.justNavigated = false;
          }, 5000),
        );
      }, 150),
    );
  }

  protected openSignupPage() {
    this.closeAllMenus();
    return this.externalService.goToSignup({ target: '_self' });
  }

  protected openLoginPage() {
    this.closeAllMenus();
    return this.externalService.goToLogin();
  }

  protected menuOpened(): void {
    // Prevent the menu re-opening right after a navigation (hydration glitch).
    if (this.justNavigated) {
      this.trigger()?.closeMenu();
      this.isMenuOpen.set(false);
      return;
    }

    this.reversedFlag.set(
      this.languages.find((language) => language.code !== this.language().code)
        ?.flag,
    );

    this.isMenuOpen.set(true);

    // Close search when the menu opens to prevent conflicts.
    if (this.isSearchVisible()) {
      this.isSearchVisible.set(false);
    }
  }

  protected menuClosed(): void {
    this.isMenuOpen.set(false);

    if (!this.isBrowser) {
      return;
    }

    // Ensure the menu stays closed even if something tries to re-open it.
    this.timeoutIds.push(
      setTimeout(() => {
        this.forceCloseMenuPanels();
        this.isMenuOpen.set(false);
      }, 10),
    );
  }

  protected goToUserHappyHelper(): void {
    if (!this.isBrowser) {
      return;
    }

    window.open(
      'https://happyhelper.dk?utm_medium=website_menu&utm_source=handyhand&utm_campaign=happy_handy_colab',
    );
  }

  private closeAllMenus(): void {
    this.isMenuOpen.set(false);
    this.isSearchVisible.set(false);
    this.isDropdownOpen.set(false);
    this.trigger()?.closeMenu();
  }

  private setupRouterNavigationListener(): void {
    this.router.events
      .pipe(
        takeUntil(this.destroy$),
        filter((event) => event instanceof NavigationEnd),
      )
      .subscribe(() => {
        this.isMenuOpen.set(false);

        if (this.isBrowser) {
          this.timeoutIds.push(
            setTimeout(() => {
              this.forceCloseMenuPanels();
            }, 100),
          );
        }

        this.resetHeaderState();
      });
  }

  private resetHeaderState(): void {
    // Reset UI state but preserve authentication status.
    this.isMenuOpen.set(false);
    this.isSearchVisible.set(false);
    this.isDropdownOpen.set(false);
    this.trigger()?.closeMenu();

    if (this.isBrowser) {
      this.removeOverlayBackdrops();
      document.body.style.pointerEvents = 'auto';

      // Notify other components that the header menu was reset.
      window.dispatchEvent(
        new CustomEvent('headerMenuReset', {
          detail: { timestamp: Date.now() },
        }),
      );
    }
  }

  private closeMobileMenuOnBackButton(): void {
    if (!this.isBrowser) {
      return;
    }

    this.isMenuOpen.set(false);
    this.trigger()?.closeMenu();
    this.forceCloseMenuPanels();
  }

  /** Force every Material menu panel hidden and clear its overlay backdrops. */
  private forceCloseMenuPanels(): void {
    if (!this.isBrowser) {
      return;
    }

    document
      .querySelectorAll<HTMLElement>('.mat-menu-panel, .mobile_nav_no_auth')
      .forEach((panel) => {
        panel.style.display = 'none';
        panel.style.visibility = 'hidden';
        panel.style.opacity = '0';
        panel.style.pointerEvents = 'none';
      });

    this.removeOverlayBackdrops();

    document.querySelector('.header_container')?.classList.remove('menu-open');
  }

  private removeOverlayBackdrops(): void {
    document
      .querySelectorAll('.cdk-overlay-backdrop')
      .forEach((backdrop) => backdrop.remove());
  }
}
